import { useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useHistory } from 'react-router-dom'

import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete';
import LowPriorityIcon from '@mui/icons-material/LowPriority'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PowerIcon from '@mui/icons-material/Power';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import CheckIcon from '@mui/icons-material/Check';

import { constants } from '../../Constants/constants'
import { setSurveyMapping, setSurveyMappingValue } from '../../actions/surveyMappingAction'
import { EditCreateDisplay } from '../../Component/EditCreateDisplay/EditCreateDisplay'
import { GlobalDialog } from '../../Component/GlobalDialog'
import { getContent } from '../../Component/EditCreateDisplay/EditUtil'
import { TempAutoSelectInput } from '../../Component/Input/TempAutoSelectInput'
import { SurveyListWithDrawer } from './SurveyListWithDrawer'
import { useSurveyMappingForm } from '../../hooks/useSurveyMappingForm'
import { surveyService } from '../../services/surveyService'

const resource = 'survey'

const labelsKeys = ['status']

export const SurveyMappingEdit = () => {

  const dispatch = useDispatch()
  const store = useStore()
  const params = useParams()
  const history = useHistory()

  const savedSurveyMapping = useSelector((state) => state.surveyMapping.ui.surveyMapping)

  const {
    dialog,
    clear,
    ready,
    saving,
    setClear,
    setReady,
    showDialog,
    closeDialog,
    markDirty,
    updateMapping,
    deleteMapping,
    toggleMappingStatus,
  } = useSurveyMappingForm()

  // Navigation helpers
  const navigateBackToEdit = useCallback(() => {
    closeDialog()
    setClear(true)
  }, [closeDialog, setClear])

  const navigateBackToList = useCallback(() => {
    closeDialog()
    history.push(
      '/' +
      constants.PATH.SHOP.SYSTEM_GROUP_RESTAURANT +
      '/' +
      params.restUrlId +
      '/' +
      constants.RESTAURANT_TAB.SURVEY +
      '/' +
      constants.RESTAURANT_SURVEY_SUB_TUB.SURVEY_MAPPING_LIST
    )
  }, [closeDialog, history, params.restUrlId])

  // Show success dialog after save/status operations
  const showSuccessDialog = useCallback(() => {
    showDialog({
      onClose: navigateBackToEdit,
      dialogContentText: { key: 'editSuccess' },
      dialogActions: [
        { label: { key: 'backEdit' }, onClick: navigateBackToEdit, icon: <ArrowBackIcon /> },
        { label: { key: 'backList' }, onClick: navigateBackToList, icon: <LowPriorityIcon /> },
      ],
    })
  }, [showDialog, navigateBackToEdit, navigateBackToList])

  // Input change handler
  function onChangeInput(name, e) {
    switch (name) {
      case 'startTimestamp':
      case 'endTimestamp':
        dispatch(setSurveyMappingValue(name, savedSurveyMapping, '', new Date(e).getTime()))
        break
      default:
        dispatch(setSurveyMappingValue(name, savedSurveyMapping, '', e))
    }
  }

  // Save
  const onClickSave = async () => {
    const success = await updateMapping(params.id, savedSurveyMapping)
    if (success) {
      showSuccessDialog()
    }
  }

  // Clear form (reload data)
  const onClickClear = () => {
    setClear(true)
  }

  // Toggle status
  const handleToggleStatus = async () => {
    const success = await toggleMappingStatus(params.id, savedSurveyMapping.status)
    if (success) {
      showSuccessDialog()
    }
  }

  // Confirm delete
  const confirmToDelete = () => {
    showDialog({
      onClose: navigateBackToEdit,
      dialogContentText: { key: 'deleteContent' },
      dialogActions: [
        { label: { key: 'cancelBtn' }, onClick: navigateBackToEdit, icon: <ArrowBackIcon /> },
        { label: { key: 'confirm' }, onClick: handleDelete, icon: <CheckIcon /> },
      ],
    })
    markDirty()
  }

  // Delete
  const handleDelete = async () => {
    await deleteMapping(params.id)
    navigateBackToEdit()
    markDirty()
  }

  // Fetch mapping data on mount / clear
  useEffect(() => {
    if (clear === true) {
      surveyService.fetchMappingDetail(params.id, store).then((payload) => {
        dispatch(setSurveyMapping(payload))
        setReady(true)
        setClear(false)
      }).catch((err) => {
        console.error('Failed to fetch survey mapping:', err)
        setReady(true)
        setClear(false)
      })
    }
  }, [clear, params.id, dispatch, setReady, setClear])

  const tabs = [
    {
      label: { resource: resource, key: 'info' },
      tabIndex: constants.RESTAURANT_SURVEY_SUB_TUB.SURVEY_MAPPING_LIST,
    },
  ]

  const tempMerchantUserEditList = [
    {
      type: constants.TYPE.TITLE,
      value: { resource: resource, key: '' },
      className: 'sub-title',
      labelEntries: Object.entries(savedSurveyMapping).filter(([key]) => labelsKeys.includes(key)),
      labelsDateKeys: [],
      translateValue: true
    },
    {
      className: 'edit-create-input',
      size: constants.SIZE.SMALL,
      label: { resource: resource, key: 'refCode' },
      value: savedSurveyMapping.refCode,
      variant: constants.STYLE.VARIANT.STANDARD,
      disabled: true,
      name: 'refCode',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      size: constants.SIZE.SMALL,
      label: { resource: resource, key: 'refType' },
      variant: constants.STYLE.VARIANT.STANDARD,
      value: savedSurveyMapping.refType,
      disabled: true,
      name: 'refType',
      isEndOfLine: true,
    },
    {
      className: 'edit-create-input',
      size: constants.SIZE.SMALL,
      label: { resource: resource, key: 'surveyId' },
      type: constants.TYPE.FUNCTION,
      value: (
        <SurveyListWithDrawer
          resource={resource}
          resourceKey={'surveyId'}
          value={savedSurveyMapping.surveyId}
          onChangeInput={onChangeInput}
          restUrlId={params.id}
          clear={false}
        />
      ),
      variant: constants.STYLE.VARIANT.STANDARD,
      name: 'surveyId',
      isEndOfLine: true,
    },
    // {
    //   className: 'edit-create-input',
    //   size: constants.SIZE.SMALL,
    //   label: { resource: resource, key: 'refType' },
    //   type: constants.TYPE.FUNCTION,
    //   value: (
    //     <TempAutoSelectInput
    //       inputSetting={{
    //         label: { resource: resource, key: 'refType' },
    //         value: savedSurveyMapping.refType,
    //         selectValue: constants.CHOICES.SURVEY.REF_TYPE,
    //         name: 'refType',
    //         disableClearable: true,
    //         // helperText: validation.getErrMsg(validationConstants.KEY_STATUS),
    //       }}
    //       handleChange={onChangeInput}
    //     />
    //   ),
    //   isEndOfLine: false,
    // },
    {
      className: 'edit-create-input',
      variant: constants.STYLE.VARIANT.STANDARD,
      size: constants.SIZE.SMALL,
      label: { resource: resource, key: 'serviceType' },
      type: constants.TYPE.FUNCTION,
      value: (
        <TempAutoSelectInput
          inputSetting={{
            label: { resource: resource, key: 'serviceType' },
            value: savedSurveyMapping.serviceType,
            selectValue: constants.CHOICES.SURVEY.SERVICE_TYPE,
            name: 'serviceType',
            disableClearable: true,
            // helperText: validation.getErrMsg(validationConstants.KEY_STATUS),
          }}
          handleChange={onChangeInput}
        />
      ),
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      size: constants.SIZE.SMALL,
      label: { resource: resource, key: 'surveyType' },
      type: constants.TYPE.FUNCTION,
      value: (
        <TempAutoSelectInput
          inputSetting={{
            label: { resource: resource, key: 'surveyType' },
            value: savedSurveyMapping.surveyType,
            selectValue: constants.CHOICES.SURVEY.SURVEY_TYPE,
            name: 'surveyType',
            disableClearable: true,
            // helperText: validation.getErrMsg(validationConstants.KEY_STATUS),
          }}
          handleChange={onChangeInput}
        />
      ),
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      size: constants.SIZE.SMALL,
      variant: constants.STYLE.VARIANT.STANDARD,
      label: { resource: resource, key: 'mandatory' },
      type: constants.TYPE.FUNCTION,
      value: (
        <TempAutoSelectInput
          inputSetting={{
            label: { resource: resource, key: 'mandatory' },
            value: savedSurveyMapping.mandatory,
            selectValue: constants.CHOICES.SURVEY.MANDATORY,
            name: 'mandatory',
            disableClearable: true,
            // helperText: validation.getErrMsg(validationConstants.KEY_STATUS),
          }}
          handleChange={onChangeInput}
        />
      ),
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      size: constants.SIZE.SMALL,
      label: { resource: resource, key: 'startTimestamp' },
      type: constants.TYPE.DATETIME_LOCAL,
      value: savedSurveyMapping.startTimestamp,
      name: 'startTimestamp',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      size: constants.SIZE.SMALL,
      label: { resource: resource, key: 'endTimestamp' },
      type: constants.TYPE.DATETIME_LOCAL,
      value: savedSurveyMapping.endTimestamp,
      name: 'endTimestamp',
      isEndOfLine: false,
    }
  ]

  const content = [
    {
      tab: tempMerchantUserEditList.map((inputSetting, index) => {
        let content = getContent(inputSetting, index, onChangeInput)
        return (
          <div className="edit-display-wrapper" key={index}>
            {content}
            {inputSetting.isEndOfLine && <div></div>}
          </div>
        )
      }),
      btn: true,
    }
  ]

  const bottomBtns = [
    {
      variant: constants.STYLE.VARIANT.CONTAINED,
      color: constants.STYLE.PRIMARY,
      label: { key: 'save' },
      onClick: onClickSave,
      className: 'margin-left-16',
      icon: <SaveIcon />,
      disabled: saving,
    },
    {
      variant: constants.STYLE.VARIANT.CONTAINED,
      color: constants.STYLE.PRIMARY,
      label: { key: 'clear' },
      disabled: false,
      onClick: onClickClear,
      className: 'margin-left-16',
      icon: <ClearIcon />,
    },
    {
      variant: constants.STYLE.VARIANT.CONTAINED,
      color: savedSurveyMapping.status == 'A' ? constants.STYLE.ERROR : constants.STYLE.SUCCESS,
      icon: savedSurveyMapping.status == 'A' ? <PowerOffIcon /> : <PowerIcon />,
      label: { key: savedSurveyMapping.status == 'A' ? 'disabled' : 'enabled' },
      className: 'special-button-margin-left margin-right-16',
      onClick: handleToggleStatus,
      disabled: saving,
    },
    {
      variant: constants.STYLE.VARIANT.CONTAINED,
      color: constants.STYLE.ERROR,
      icon: <DeleteIcon />,
      label: { key: 'deleteBtn' },
      className: 'special-button margin-right-16',
      onClick: confirmToDelete,
      disabled: saving,
    },
  ]

  return (
    ready && (
      <>
        <EditCreateDisplay
          code={savedSurveyMapping && savedSurveyMapping.id ? savedSurveyMapping.id : ''}
          resource={resource}
          tabs={tabs}
          content={content}
          onChangeInput={onChangeInput}
          bottomBtns={bottomBtns}
          display="edit"
        />
        {dialog && dialog.open && <GlobalDialog dialogInfo={dialog} />}
      </>
    )
  )
}
